/**
 * PillarRow — accordion row for a single pillar (title + score) inside a
 * SummariesCard column. On expand: shows pillar summary text, score
 * rationale, proof points, optional watch-out, the PillarRatingWidget,
 * and the PillarSourcesPanel drill-down.
 *
 * Spec: docs/superpowers/specs/2026-05-28-admin-matches-summaries-design.md §7.2
 */
import { useState } from 'react';
import { useAdminPermissions } from '@/contexts/AdminPermissionsContext';
import { PillarSourcesPanel } from './PillarSourcesPanel';
import { PillarRatingWidget } from './PillarRatingWidget';

export interface PillarShape {
  title: string;
  score: number;
  summary?: string;
  score_explanation?: string;
  proof_points?: string[];
  watch_out?: string | null;
}

interface Props {
  pillar: PillarShape;
  pillarIndex: number;
  matchId: string;
  forUserId: string;
  locale: 'en' | 'es';
  user1Label?: string;
  user2Label?: string;
}

export function PillarRow({
  pillar,
  pillarIndex,
  matchId,
  forUserId,
  locale,
  user1Label,
  user2Label,
}: Props) {
  const [open, setOpen] = useState(false);
  const { hasPermission } = useAdminPermissions();

  return (
    <div className="border-t" style={{ padding: '8px 4px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center justify-between border-none bg-transparent p-0 text-left text-[13px] text-foreground"
      >
        <span style={{ fontWeight: 600 }}>
          {open ? '▼' : '▶'} {pillar.title}
        </span>
        <span className={`font-semibold ${scoreColorClass(pillar.score)}`}>
          {pillar.score}
          {pillar.score >= 90 && ' ⭐'}
        </span>
      </button>

      {open && (
        <div style={{ paddingLeft: 16, marginTop: 6 }}>
          {pillar.summary && (
            <p style={{ margin: '4px 0', fontSize: 13 }}>{pillar.summary}</p>
          )}
          {pillar.score_explanation && (
            <p className="my-1 text-xs text-muted-foreground">
              <em>Score:</em> {pillar.score_explanation}
            </p>
          )}
          {pillar.proof_points && pillar.proof_points.length > 0 && (
            <ul style={{ margin: '4px 0', paddingLeft: 18, fontSize: 12 }}>
              {pillar.proof_points.map((pp, i) => (
                <li key={i}>{pp}</li>
              ))}
            </ul>
          )}
          {pillar.watch_out && (
            <p className="my-1 rounded bg-amber-500/10 px-1.5 py-1 text-xs text-amber-600 dark:text-amber-400">
              <strong>Watch out:</strong> {pillar.watch_out}
            </p>
          )}

          {hasPermission('matches.summary.rate') && (
            <PillarRatingWidget
              matchId={matchId}
              forUserId={forUserId}
              locale={locale}
              pillarIndex={pillarIndex}
            />
          )}

          <PillarSourcesPanel
            matchId={matchId}
            pillarTitle={pillar.title}
            user1Label={user1Label}
            user2Label={user2Label}
          />
        </div>
      )}
    </div>
  );
}

function scoreColorClass(s: number): string {
  if (s >= 85) return 'text-green-500';
  if (s >= 65) return 'text-amber-500';
  return 'text-red-500';
}
